// AuthFlowGate — глобальный гейт обязательных post-login flow.
//
// Пользователь, вошедший через IEC ID, ОБЯЗАН пройти три шага, прежде чем
// получит доступ к приложению:
//   0. iecLegalPending    → auth.iec.exchange/consent  (блокирующая редакция
//                            документа ЭКОСИСТЕМЫ; принимается у них, не у нас —
//                            docs/INTEGRATION-GUIDE.md §3.2)
//   1. pendingConsent     → /legal/accept  (наш дисклеймер об ИИ-генерации —
//                            152-ФЗ ст.6/9 + GDPR Art.13 + AI disclosure)
//   1b. (только приложение) разрешение на передачу данных AI-сервисам — экран
//                            `AiDataSharingPrompt` на КАЖДОЙ установке, раньше
//                            онбординга, где уже создаётся первый курс
//                            (App Store 5.1.1(i) / 5.1.2(i), отказ 17.09.2026)
//   2. pendingOnboarding   → /onboarding   (онбординг: язык → метод → первый
//                            курс → ритм → готово)
//
// Монтируется в __root вокруг Outlet. Пока редирект «в полёте» — рендерит
// fullscreen-loader вместо контента, чтобы не мелькала защищённая страница
// (дашборд) перед уводом на экран согласия.
//
// Инварианты (rules/frontend-state.md §5.3):
//  - isOffline → НЕ редиректим: офлайн cold start не должен выкидывать
//    залогиненного на гейт (synthOfflineUser отдаёт pending*=false, но guard
//    дублируем явно — на случай реального серверного ответа из персиста).
//  - Гость (!isAuthenticated) и время загрузки сессии (isLoading) — пропускаем.
//  - Exempt-префиксы: сам consent-экран, статичные юр-страницы, весь auth-flow
//    (login/callback/logout) и онбординг — рендерятся без редиректа, иначе
//    петля (нельзя редиректить /legal/accept сам на себя) и поломка logout.

import { IconLoader2 } from "@tabler/icons-react";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { AiDataSharingPrompt } from "@/components/legal/ai-data-sharing-prompt";
import { markAskedOnThisInstall, wasAskedOnThisInstall } from "@/lib/ai-data-sharing-install";
import { IS_CAPACITOR } from "@/lib/platform-detect";
import { isProtectedPath } from "@/lib/protected-routes";
import { publicWebOrigin } from "@/lib/web-origin";
import { useAuth } from "@/providers/auth-provider";
import { useLoginModalStore } from "@/stores/login-modal";
import { needsAiDataSharingStep } from "./ai-data-sharing-step";
import { isExternalAuthFlowTarget, resolveAuthFlowTarget, underPrefix } from "./auth-flow-target";

/**
 * Отметка «на экосистемное согласие уже уводили в этой вкладке».
 *
 * Живёт в `sessionStorage`, а не в памяти: единственный момент, когда она
 * нужна, — это возврат с чужого домена, то есть новая загрузка страницы.
 */
const IEC_CONSENT_REDIRECT_KEY = "iec-id:consent-redirect-sent";

function readIecConsentRedirectSent(): boolean {
	try {
		return window.sessionStorage.getItem(IEC_CONSENT_REDIRECT_KEY) === "1";
	} catch {
		// Приватный режим / запрет хранилища: остаётся прежнее поведение «одна
		// попытка за загрузку». Хуже, чем с отметкой, но не хуже, чем было.
		return false;
	}
}

function writeIecConsentRedirectSent(sent: boolean): void {
	try {
		const storage = window.sessionStorage;
		if (sent) storage.setItem(IEC_CONSENT_REDIRECT_KEY, "1");
		else storage.removeItem(IEC_CONSENT_REDIRECT_KEY);
	} catch {
		// Запись отметки — страховка, а не инвариант: ронять из-за неё гейт входа
		// нельзя.
	}
}

/**
 * Уход на чужой домен.
 *
 * Web — текущей вкладкой: человек вернётся по `return_to` на тот же адрес.
 * Capacitor — СИСТЕМНЫМ браузером: навигация WebView увела бы приложение с
 * бандла, и вернуть его оттуда нечем — обратно человека втянет universal link
 * на `study.iec.exchange`. Ровно тот же приём, что у перехода в кассу
 * экосистемы (`components/billing/_use-iec-checkout.ts`), и по той же причине.
 */
function leaveToExternal(url: string): void {
	if (IS_CAPACITOR) {
		window.open(url, "_blank", "");
		return;
	}
	window.location.assign(url);
}

export function AuthFlowGate({ children }: { children: ReactNode }): React.ReactElement {
	const navigate = useNavigate();
	const pathname = useRouterState({ select: (s) => s.location.pathname });
	const {
		user,
		isAuthenticated,
		isOffline,
		isLoading,
		pendingConsent,
		iecLegalPending,
		pendingOnboarding,
	} = useAuth();

	// Один увод на их `/consent` за ВКЛАДКУ — не за загрузку страницы.
	//
	// Разница принципиальная, и прежний `useRef(false)` её терял. Увод — это
	// уход с домена целиком, а возврат по `return_to` — НОВАЯ загрузка: ref на
	// ней обнулялся, и ограничитель не защищал ни от чего. Замкнуть круг
	// достаточно одного условия: наш `iec_legal_pending` гасит только успешный
	// вход (`clearIecLegalPending` в `auth.config.ts` — их гейт стоит на
	// `authorize`), а возврат с `/consent` входом не является. Человек с живой
	// сессией, уже принявший редакцию у них, ходил бы study → auth → study по
	// кругу.
	//
	// `sessionStorage` переживает переход между доменами и умирает вместе с
	// вкладкой. Отметка снимается, как только сервер сказал «непринятого нет»
	// (эффект ниже), — иначе следующая блокирующая редакция в той же вкладке
	// была бы проигнорирована.
	const iecConsentRedirectSentRef = useRef(readIecConsentRedirectSent());

	useEffect(() => {
		if (iecLegalPending || !iecConsentRedirectSentRef.current) return;
		iecConsentRedirectSentRef.current = false;
		writeIecConsentRedirectSent(false);
	}, [iecLegalPending]);

	// Derived (не useEffect+setState): какой обязательный шаг недопройден здесь.
	const target = resolveAuthFlowTarget({
		pathname,
		isLoading,
		isOffline,
		isAuthenticated,
		pendingConsent,
		iecLegalPending,
		iecConsentRedirectSent: iecConsentRedirectSentRef.current,
		// НЕ `window.location.origin`: в Capacitor это `capacitor://localhost`,
		// а `return_to` обязан быть абсолютным https-адресом на НАШ домен —
		// чужой они молча игнорируют (§1.4, §3.2), и человек остался бы у них.
		origin: publicWebOrigin(),
		pendingOnboarding,
	});

	useEffect(() => {
		if (!target) return;
		if (isExternalAuthFlowTarget(target)) {
			// Уход с домена целиком — `navigate()` роутера сюда не годится.
			// Отметку пишем ДО перехода: после ухода этот код уже не выполнится.
			iecConsentRedirectSentRef.current = true;
			writeIecConsentRedirectSent(true);
			leaveToExternal(target.externalUrl);
			return;
		}
		void navigate({ to: target, replace: true });
	}, [target, navigate]);

	// Гость на закрытом роуте (включая служебные экраны онбординга) → редирект
	// на "/" + открытие модалки входа (returnTo = исходный путь), как в IEC Hero.
	// isOffline не трогаем — офлайн cold start залогиненного не выкидываем.
	//
	// Короткий путь «человек уже вошёл в другом сервисе экосистемы» (§1.6) здесь
	// не запускается — и нигде на монтировании тоже. Он живёт в обработчике
	// клика по «Войти» (`hooks/use-oauth-login.ts` → `lib/iec-session-shortcut.ts`):
	// проба на открытии окна уносила вошедшего в приложение до того, как он
	// что-либо нажал. Гейт — `login-modal-no-autologin.test.ts`.
	const isRestrictedFlow = underPrefix(pathname, ["/onboarding", "/legal/accept"]);
	const guestBlocked =
		!isLoading && !isOffline && !isAuthenticated && (isProtectedPath(pathname) || isRestrictedFlow);

	useEffect(() => {
		if (!guestBlocked) return;
		const returnTo = window.location.pathname + window.location.search;
		useLoginModalStore.getState().open(returnTo);
		void navigate({ to: "/", replace: true });
	}, [guestBlocked, navigate]);

	// Разрешение на передачу данных AI — после документов, до всего остального
	// (`ai-data-sharing-step.ts`). Отметка установки лежит в localStorage, а оно
	// не реактивно: после ответа гейт узнаёт о нём из своего состояния, иначе
	// экран остался бы висеть до следующего рендера по другой причине.
	const userId = user?.id ?? null;
	const [askedFor, setAskedFor] = useState<{ userId: string; asked: boolean } | null>(null);
	const askedHere =
		userId === null
			? true
			: askedFor?.userId === userId
				? askedFor.asked
				: wasAskedOnThisInstall(userId);
	const needsAiPermission = needsAiDataSharingStep({
		isNativeApp: IS_CAPACITOR,
		userId,
		askedOnThisInstall: askedHere,
		isOffline,
		isLoading,
		pendingConsent,
		iecLegalPending,
		pathname,
	});

	if (target || guestBlocked) return <GateLoader />;
	if (needsAiPermission && userId !== null) {
		return (
			<AiDataSharingPrompt
				onDecided={() => {
					markAskedOnThisInstall(userId);
					setAskedFor({ userId, asked: true });
				}}
			/>
		);
	}
	return <>{children}</>;
}

function GateLoader(): React.ReactElement {
	return (
		<div className="flex min-h-dvh items-center justify-center bg-background">
			<div className="flex size-12 items-center justify-center rounded-[var(--radius-tile)] bg-primary/10 shadow-e1">
				<IconLoader2 size={24} className="animate-spin text-primary" stroke={1.75} />
			</div>
		</div>
	);
}
